import { Loader2, ShoppingCart } from 'lucide-react'

import { cn } from '@/lib/utils'

import { Button } from '../../Theme'
import { LABELS } from '../../UiBlocks'
import { useCheckout } from '../state'

interface AddToCartButtonProps {
  /** Product SKU */
  sku: string
  /** Quantity to add (default: 1) */
  quantity?: number
  /** Disabled state */
  disabled?: boolean
  /** Button label (default: LABELS.ADD_TO_CART) */
  label?: string
  /** Custom className */
  className?: string
  /** Label only below md, cart icon only from md up (default: icon below md, label + icon from md up) */
  iconFromMd?: boolean
}

/**
 * AddToCartButton - Button component for adding products to cart
 *
 * Uses CheckoutProvider context for cart operations.
 * Shows loading spinner when adding, text + cart icon when idle.
 *
 * @example
 * // Inside a component wrapped with CheckoutProvider
 * <AddToCartButton sku="SKU123" />
 */
export function AddToCartButton({
  sku,
  quantity = 1,
  disabled = false,
  label = LABELS.ADD_TO_CART,
  className,
  iconFromMd = false,
}: AddToCartButtonProps) {
  const { handleAddToCart, addingToCart } = useCheckout()

  const isLoading = addingToCart === sku

  const handleClick = () => handleAddToCart({ sku, quantity })

  return (
    <Button
      variant="addToCart"
      size="addToCart"
      onClick={handleClick}
      disabled={isLoading || disabled}
      aria-busy={isLoading}
      aria-label={isLoading ? 'Se adauga in cos...' : label}
      className={cn('relative', className)}
    >
      {/* Spinner overlay - appears on top when loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="animate-spin w-6 h-6" aria-hidden="true" />
        </div>
      )}
      {/* Content - stays in place but becomes invisible when loading */}
      <div className={cn('flex items-center', isLoading && 'invisible')}>
        <span className={cn(
          'font-main-font text-sm group-hover/addtocart:text-text-color transition-colors duration-300',
          iconFromMd ? 'md:hidden' : 'hidden md:inline',
        )}>
          {label}
        </span>
        <span className={cn(
          'group-hover/addtocart:text-text-color transition-colors duration-300',
          iconFromMd ? 'hidden md:inline' : 'md:ml-5',
        )}>
          <ShoppingCart size={24} aria-hidden="true" />
        </span>
      </div>
    </Button>
  )
}
